import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useGetAllResources } from "@/api/ResourcesApi"; // adjust path if needed
import { useResourceStore } from "@/stores/resourceStore";
import ResourceCard from "@/components/resources/ResourceCard";
import ResourceFilters from "@/components/resources/ResourceFilters";
import ResourceStats from "@/components/resources/ResourceStats";

const ResourcesPage: React.FC = () => {
  const { loading, getAllResources } = useGetAllResources();
  const resources = useResourceStore((s) => s.resources);

  // Local UI state
  const [query, setQuery] = useState("");
  const [filterType, setFilterType] = useState<string>("All");
  const [sortBy, setSortBy] = useState<string>("most_downloaded"); // UI default
  const [viewGrid, setViewGrid] = useState(true);

  useEffect(() => {
    getAllResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // derive categories from resources (type field)
  const categories = useMemo(() => {
    if (!resources) return [];
    const set = new Set<string>();
    resources.forEach((r) => r.type && set.add(r.type));
    return Array.from(set);
  }, [resources]);

  // Filter & search
  const filtered = useMemo(() => {
    if (!resources) return [];
    let list = [...resources];

    if (filterType && filterType !== "All") {
      list = list.filter((r) => r.type === filterType);
    }

    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (r) =>
          (r.title && r.title.toLowerCase().includes(q)) ||
          (r.description && r.description.toLowerCase().includes(q)),
      );
    }

    // Sorting - API doc doesn't mention download counts, so sorts are best-effort:
    switch (sortBy) {
      case "most_downloaded":
        // Not available in API docs; keep current order
        break;
      case "latest":
        list.sort((a, b) => {
          const ta = a.published_date
            ? new Date(a.published_date).getTime()
            : 0;
          const tb = b.published_date
            ? new Date(b.published_date).getTime()
            : 0;
          return tb - ta;
        });
        break;
      case "az":
        list.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        break;
      default:
        break;
    }

    return list;
  }, [resources, query, filterType, sortBy]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-semibold">Resource Library</h1>
          <p className="text-sm text-slate-500">
            Access training materials, guides, and templates
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/resources/upload"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M3 16v1a2 2 0 002 2h14a2 2 0 002-2v-1"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7 10l5-5 5 5"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 5v12"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Upload Resource
          </Link>
        </div>
      </div>

      <ResourceStats resources={resources || []} />

      <div className="mt-6">
        <ResourceFilters
          query={query}
          setQuery={setQuery}
          categories={["All", ...categories]}
          filterType={filterType}
          setFilterType={setFilterType}
          sortBy={sortBy}
          setSortBy={setSortBy}
          viewGrid={viewGrid}
          setViewGrid={setViewGrid}
        />
      </div>

      <div className="mt-6">
        <p className="text-sm text-slate-500 mb-3">
          Showing {filtered.length}{" "}
          {filtered.length === 1 ? "resource" : "resources"}
        </p>

        {loading && (
          <div className="text-sm text-slate-500">Loading resources...</div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No resources found.
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div
            className={
              viewGrid
                ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                : "space-y-4"
            }
          >
            {filtered.map((res) => (
              <ResourceCard key={res.id} resource={res} grid={viewGrid} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourcesPage;
